/**
 * AppointmentDetailModal
 * Feature: 005-clinic-scheduling (T026, T027)
 *
 * Modal showing appointment details with cancel and reschedule actions.
 */

import { useState } from 'react'
import {
  CalendarDays,
  User,
  Users,
  FileText,
  CalendarX,
  CalendarClock,
  Loader2,
  Repeat,
  MessageCircle,
  MapPin,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import {
  useCancelSession,
  useRescheduleSession,
} from '../hooks/use-appointments'
import { EditScopeDialog } from './EditScopeDialog'
import {
  SESSION_STATUS_LABELS,
  SESSION_STATUS_COLORS,
  type CalendarEventExtendedProps,
  type EditScope,
} from '../types/scheduling.types'

interface AppointmentDetailModalProps {
  event: CalendarEventExtendedProps | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AppointmentDetailModal({
  event,
  open,
  onOpenChange,
}: AppointmentDetailModalProps) {
  const { toast } = useToast()
  const cancelSession = useCancelSession()
  const rescheduleSession = useRescheduleSession()

  const [showScopeDialog, setShowScopeDialog] = useState(false)
  const [scopeAction, setScopeAction] = useState<
    'cancel' | 'reschedule' | null
  >(null)
  const [showRescheduleForm, setShowRescheduleForm] = useState(false)
  const [newDate, setNewDate] = useState('')
  const [newStartTime, setNewStartTime] = useState('')
  const [newEndTime, setNewEndTime] = useState('')

  if (!event) return null

  const statusColors = SESSION_STATUS_COLORS[event.status]
  const isCancelled = event.status === 'cancelled'

  const handleCancelClick = () => {
    if (event.is_recurring) {
      setScopeAction('cancel')
      setShowScopeDialog(true)
    } else {
      executeCancelSession('this_only')
    }
  }

  const handleRescheduleClick = () => {
    setShowRescheduleForm(true)
  }

  const handleScopeSelect = (scope: EditScope) => {
    if (scopeAction === 'cancel') {
      executeCancelSession(scope)
    } else if (scopeAction === 'reschedule') {
      executeRescheduleSession(scope)
    }
    setScopeAction(null)
  }

  const executeCancelSession = async (scope: EditScope) => {
    const result = await cancelSession.mutateAsync({
      session_id: event.session_id,
      scope,
    })

    if (result.success) {
      toast({
        title: 'Atendimento cancelado',
        description:
          scope === 'this_and_future'
            ? 'Este e todos os atendimentos futuros foram cancelados.'
            : 'O atendimento foi cancelado.',
      })
      onOpenChange(false)
    } else {
      toast({
        title: 'Erro',
        description: result.error ?? 'Erro ao cancelar atendimento.',
        variant: 'destructive',
      })
    }
  }

  const handleRescheduleSubmit = () => {
    if (!newDate || !newStartTime || !newEndTime) {
      toast({
        title: 'Campos obrigatorios',
        description: 'Preencha a nova data e horarios.',
        variant: 'destructive',
      })
      return
    }

    if (newEndTime <= newStartTime) {
      toast({
        title: 'Horario invalido',
        description: 'O horario de termino deve ser posterior ao de inicio.',
        variant: 'destructive',
      })
      return
    }

    if (event.is_recurring) {
      setScopeAction('reschedule')
      setShowScopeDialog(true)
    } else {
      executeRescheduleSession('this_only')
    }
  }

  const executeRescheduleSession = async (scope: EditScope) => {
    const result = await rescheduleSession.mutateAsync({
      session_id: event.session_id,
      new_date: newDate,
      new_start_time: newStartTime,
      new_end_time: newEndTime,
      scope,
    })

    if (result.success) {
      toast({
        title: 'Atendimento remarcado',
        description:
          scope === 'this_and_future'
            ? 'Este e todos os atendimentos futuros foram remarcados.'
            : 'O atendimento foi remarcado.',
      })
      onOpenChange(false)
      setShowRescheduleForm(false)
    } else {
      toast({
        title: 'Erro',
        description: result.error ?? 'Erro ao remarcar atendimento.',
        variant: 'destructive',
      })
    }
  }

  const isProcessing = cancelSession.isPending || rescheduleSession.isPending

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle
              className={`flex items-center gap-2 ${isCancelled ? 'line-through text-muted-foreground' : ''}`}
            >
              <User className="h-5 w-5" />
              {event.patient_name}
            </DialogTitle>
            <DialogDescription className="flex items-center gap-2">
              <Badge
                style={{
                  backgroundColor: statusColors.bg,
                  color: statusColors.text,
                  borderColor: statusColors.border,
                }}
              >
                {SESSION_STATUS_LABELS[event.status]}
              </Badge>
              {event.is_recurring && (
                <Badge variant="outline" className="gap-1">
                  <Repeat className="h-3 w-3" />
                  Recorrente
                </Badge>
              )}
              {event.is_exception && <Badge variant="secondary">Exceção</Badge>}
              {(event.whatsapp_admin_request_count ?? 0) > 0 && (
                <Badge variant="outline" className="gap-1">
                  <MessageCircle className="h-3 w-3" />
                  WhatsApp pendente
                </Badge>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="flex items-center gap-2 text-sm">
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
              <span>{event.session_type}</span>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span>
                {event.professional_names.length > 0
                  ? event.professional_names.join(', ')
                  : 'Sem profissional'}
              </span>
            </div>

            {event.location_text && (
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                <span className="text-muted-foreground">
                  {event.location_text}
                </span>
              </div>
            )}

            {event.notes && (
              <div className="flex items-start gap-2 text-sm">
                <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                <span className="text-muted-foreground">{event.notes}</span>
              </div>
            )}
          </div>

          {/* Reschedule form */}
          {showRescheduleForm && !isCancelled && (
            <div className="space-y-3 border-t pt-3">
              <h4 className="text-sm font-medium">Remarcar para:</h4>
              <div className="space-y-2">
                <div>
                  <Label>Nova data</Label>
                  <Input
                    type="date"
                    value={newDate}
                    onChange={(e) => setNewDate(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Inicio</Label>
                    <Input
                      type="time"
                      value={newStartTime}
                      onChange={(e) => setNewStartTime(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Termino</Label>
                    <Input
                      type="time"
                      value={newEndTime}
                      onChange={(e) => setNewEndTime(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleRescheduleSubmit}
                  disabled={isProcessing}
                >
                  {isProcessing && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Confirmar
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowRescheduleForm(false)}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}

          {/* Action buttons */}
          {!isCancelled && !showRescheduleForm && (
            <div className="flex gap-2 pt-2 border-t">
              <Button
                variant="destructive"
                size="sm"
                onClick={handleCancelClick}
                disabled={isProcessing}
              >
                {cancelSession.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CalendarX className="mr-2 h-4 w-4" />
                )}
                Cancelar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRescheduleClick}
                disabled={isProcessing}
              >
                <CalendarClock className="mr-2 h-4 w-4" />
                Remarcar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <EditScopeDialog
        open={showScopeDialog}
        onOpenChange={setShowScopeDialog}
        title={
          scopeAction === 'cancel'
            ? 'Cancelar atendimento recorrente'
            : 'Remarcar atendimento recorrente'
        }
        description="Este atendimento faz parte de um agendamento recorrente. O que voce deseja alterar?"
        onSelect={handleScopeSelect}
      />
    </>
  )
}
